import React, { useRef, type FormEvent } from 'react';
import './LoginScreen.css';

/**
 * F 374 — THE LOGIN SCREEN.
 *
 * WHAT THIS IS. A local unlock screen. It collects an email and a password and
 * hands them to the session, which creates the `user` node in the graph, sets
 * the Principal, and derives the AES-GCM key the credential store seals with.
 *
 * WHAT THIS IS NOT. It is not authentication. There is no server to check a
 * password against — the dispatch forbids sending one — so anyone who can
 * reach the app can sign in with any email and any password. What the password
 * buys is a KEY that only this browser knows, and an IDENTITY to scope
 * credentials to. The report says this plainly; the screen does not pretend
 * otherwise.
 *
 * WHY THE PASSWORD INPUT IS UNCONTROLLED — AND WHY THAT IS NOT A STYLE CHOICE
 * --------------------------------------------------------------------------
 * A CONTROLLED React `<input value={secret}>` writes the value into the
 * element's `value` ATTRIBUTE. So `document.documentElement.outerHTML` — and
 * anything that serialises the DOM — carries the secret, EVEN AT
 * `type="password"`. `type="password"` hides it on screen; it does not hide it
 * from the DOM.
 *
 * So the value must live only in the element's *property*, which no
 * serialisation includes. That means: no `value` prop, no `defaultValue` prop,
 * and a ref to read it. `defaultValue` is banned for a second reason — React
 * re-applies it on every re-render, so a `defaultValue=""` would reset the
 * field the moment anything re-renders, which is exactly what a "does this
 * field hold anything?" boolean does.
 *
 * The leak test in `__tests__/f374-login-screen.spec.tsx` is the load-bearing
 * guard for this. It is not a stylistic assertion.
 */

export interface LoginScreenProps {
  /**
   * Called with the email and password. Resolve `true` on success.
   *
   * The password is handed over ONCE and is never retained by this component.
   */
  onSubmit: (email: string, password: string) => Promise<boolean> | boolean;
  /** Why the last attempt failed. Null when the last attempt succeeded. */
  error?: string | null;
  /** True while a submit is in flight. */
  busy?: boolean;
  title?: string;
  subtitle?: string;
}

export const LoginScreen: React.FC<LoginScreenProps> = ({
  onSubmit,
  error,
  busy = false,
  title = 'Welcome back',
  subtitle = 'Sign in to unlock your cards',
}) => {
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const email = emailRef.current?.value ?? '';
    const password = passwordRef.current?.value ?? '';
    try {
      await onSubmit(email, password);
    } finally {
      // The password is dropped from the field whether the submit succeeded or
      // failed, so it is not left sitting in the DOM after a failed attempt.
      if (passwordRef.current) passwordRef.current.value = '';
    }
  };

  return (
    <div className="fc-login-page">
      <form data-testid="fc-login" onSubmit={handleSubmit} className="fc-login-card" noValidate>
        <div className="fc-login-header">
          <h1 className="fc-login-title">{title}</h1>
          <p className="fc-login-subtitle">{subtitle}</p>
        </div>

        <div className="fc-login-group">
          <label htmlFor="fc-login-email" className="fc-login-label">
            Email
          </label>
          <input
            id="fc-login-email"
            name="email"
            type="email"
            ref={emailRef}
            autoComplete="username"
            autoFocus
            disabled={busy}
            data-testid="fc-login-email"
            className="fc-login-input"
          />
        </div>

        <div className="fc-login-group">
          <label htmlFor="fc-login-password" className="fc-login-label">
            Password
          </label>
          <input
            id="fc-login-password"
            name="password"
            type="password"
            ref={passwordRef}
            autoComplete="current-password"
            disabled={busy}
            data-testid="fc-login-password"
            className="fc-login-input"
          />
        </div>

        <button
          type="submit"
          disabled={busy}
          data-testid="fc-login-submit"
          className="fc-login-button"
        >
          {busy ? 'Signing in…' : 'Sign in'}
        </button>

        {error ? (
          <div role="alert" data-testid="fc-login-error" className="fc-login-error">
            {error}
          </div>
        ) : null}
      </form>
    </div>
  );
};

export default LoginScreen;
